import React, { useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import { useFormikContext, getIn } from "formik";
import { AddSolutionFormValues } from "../../../add/form/types";
import { EditSolutionFormValues } from "../../../edit/form/types";

export const FileUploader = () => {
  const context = useFormikContext<
    AddSolutionFormValues | EditSolutionFormValues
  >();
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [isMouseOver, setIsMouseOver] = useState<boolean>(false);

  const fileField = "risk-monitoring.geospatial-file";
  const errorMessage =
    getIn(context.touched, fileField) && getIn(context.errors, fileField);

  const file = getIn(context.values, fileField) as File | null;
  const previouslyUploadedFileData = getIn(
    context.values,
    "risk-monitoring.geospatial-id",
  );

  const defaultMessages = {
    dragOver: "drop file here",
    mouseOver: "accepted files: default shp/kml, zipped shapefile/kml, geojson",
    empty: "geospatial file needed",
  };

  const uploadMessage = isDragOver
    ? defaultMessages.dragOver
    : isMouseOver
      ? defaultMessages.mouseOver
      : file
        ? file.name
        : previouslyUploadedFileData
          ? `previously uploaded project shape: ${previouslyUploadedFileData}`
          : defaultMessages.empty;

  const stopDefaults = (e: React.DragEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const dragEvents = {
    onMouseEnter: () => setIsMouseOver(true),
    onMouseLeave: () => setIsMouseOver(false),
    onDragEnter: (event: React.DragEvent) => {
      stopDefaults(event);
      setIsDragOver(true);
    },
    onDragLeave: (event: React.DragEvent) => {
      stopDefaults(event);
      setIsDragOver(false);
    },
    onDragOver: stopDefaults,
    onDrop: async (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      const droppedFile = event.dataTransfer?.files?.[0];
      if (!droppedFile) {
        return;
      }
      context.setFieldValue(fileField, droppedFile);
    },
  };

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target && event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target?.files?.[0];
      context.setFieldValue(fileField, selectedFile);
    }
  };

  const highlightInteraction = isDragOver || isMouseOver;
  const isMobile = useMediaQuery(`(max-width: 1100px)`);
  const fontSize = isMobile ? "0.5rem" : "1rem";

  const style = {
    message: {
      opacity: 1,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      wordBreak: "break-word",
      alignItems: "center",
      width: "100%",
      height: "100%",
      position: "absolute",
      borderRadius: "5px",
      fontWeight: 900,
      paddingTop: isMobile ? "1rem" : 0,
    },
    uploadBox: {
      cursor: "pointer",
      textAlign: "center",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      height: "100%",
      position: "absolute",
      borderRadius: "5px",
      backgroundSize: "contain",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      marginTop: isMobile ? "4rem" : 0,
    },
  };

  return (
    <>
      <input
        type="file"
        accept=".shp, .kml, .geojson, .zip"
        onChange={handleChange}
        id="file-upload"
        data-testid="geospatial-file-upload"
        aria-label="Upload geospatial file"
        width="100%"
        height="100%"
        style={{ opacity: 0 }}
      />
      <Box
        component="label"
        htmlFor="file-upload"
        {...dragEvents}
        sx={{
          ...style.uploadBox,
        }}
      >
        {uploadMessage && (
          <Box
            sx={{
              ...style.message,
              backgroundColor: highlightInteraction ? "#70e49c" : "transparent",
              border: "2px solid #70e49c",
            }}
          >
            {!isMobile && (
              <LanguageIcon
                sx={{ fontSize: isMobile ? "2rem" : "3rem", padding: "0.5rem" }}
              />
            )}
            <Typography
              sx={{
                fontSize,
                padding: "0 1rem 1rem 1rem",
              }}
            >
              {uploadMessage}
            </Typography>
          </Box>
        )}
        {errorMessage && (
          <Typography
            variant="caption"
            sx={{
              ...style.message,
              backgroundColor: "#e9ebedff",
              color: "red",
              border: "red solid 1px",
              fontSize: fontSize,
              fontWeight: 900,
              padding: "1rem",
              zIndex: 1,
            }}
          >
            {errorMessage}
          </Typography>
        )}
      </Box>
    </>
  );
};
